import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BuyerContact,
  TaskPriority,
  TaskStatus,
  TaskType,
  CollectionActivityReference,
  CollectionActivityWithUser,
} from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import {
  updateCollectionActivity,
  getBuyerCollectionActivities,
} from '../../../../../../redux/services/collectionActivityService';
import {
  getUserCustomer,
  getSupplierSummary,
} from '../../../../../../redux/services/suppliersService';
import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { AppInputField } from '../../../../../general/AppInputField/AppInputField';
import { AppDropDown } from '../../../../../general/AppDropDown/AppDropDown';
import AppDatePicker from '../../../../../general/AppDatePicker/AppDatePicker';
import { openMessageModal } from '../../../../../../redux/globalSlices/genericSlice';
import LexicalEditor from '../../../../../general/LexicalEditor';
import ActivityReferenceSelector from '../../ActivityReferenceSelector/ActivityReferenceSelector';

interface EditTaskModalProps {
  open: boolean;
  activity: CollectionActivityWithUser | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const priorityOptions: { value: TaskPriority; label: string; color: string }[] = [
  { value: 'HIGH', label: 'High', color: 'text-red-600' },
  { value: 'MEDIUM', label: 'Medium', color: 'text-yellow-600' },
  { value: 'LOW', label: 'Low', color: 'text-green-600' },
];

const statusOptions: { value: TaskStatus; label: string; color: string }[] = [
  { value: 'PENDING', label: 'Pending', color: 'text-gray-600' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: 'text-blue-600' },
  { value: 'COMPLETED', label: 'Completed', color: 'text-green-600' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'text-red-600' },
];

const taskTypeOptions: { value: TaskType; label: string }[] = [
  { value: 'TO_DO', label: 'To-Do' },
  { value: 'CALL', label: 'Call' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'TEXT_MESSAGE', label: 'Text' },
];

function EditTaskModal({ open, activity, onClose, onSuccess }: EditTaskModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { loadingList } = useAppSelector((state) => state.collectionActivities);

  const { supplierUsers } = useAppSelector((state) => state.supplier);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState<string | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM');
  const [status, setStatus] = useState<TaskStatus>('PENDING');
  const [taskType, setTaskType] = useState<TaskType>('TO_DO');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [references, setReferences] = useState<CollectionActivityReference[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [titleError, setTitleError] = useState('');
  const [buyerUsers, setBuyerUsers] = useState<
    {
      email: string;
      firstName: string;
      lastName: string;
      userId: string;
      isInvitedUser: boolean;
    }[]
  >([]);
  const [isLoadingBuyer, setIsLoadingBuyer] = useState(false);
  const [buyerContacts, setBuyerContacts] = useState<BuyerContact[]>([]);

  const isLoading = loadingList.includes('updateCollectionActivity') || isLoadingBuyer;

  // Derive initial description from activity - memoized
  const initialDescription = useMemo(() => {
    if (!activity?.taskDetails?.description) return undefined;
    const desc = activity.taskDetails.description;
    return typeof desc === 'string' ? desc : JSON.stringify(desc);
  }, [activity?.taskDetails?.description]);

  // Fetch buyer details on modal open
  const fetchBuyerDetails = useCallback(
    async (buyerId: string) => {
      if (!currentSupplier?.id) return;
      setIsLoadingBuyer(true);
      try {
        const result = await dispatch(getUserCustomer({ supplierId: currentSupplier.id, buyerId }));
        if (result.type === 'supplier/getUserCustomer/fulfilled') {
          const payload = result.payload as any;
          if (payload?.data?.usersEmailsDetails) {
            setBuyerUsers(payload.data.usersEmailsDetails);
          }
          if (payload?.data?.buyer?.buyerContacts) {
            setBuyerContacts(payload.data.buyer.buyerContacts);
          }
        }
      } catch (error) {
        console.error('Failed to fetch buyer details:', error);
      } finally {
        setIsLoadingBuyer(false);
      }
    },
    [dispatch, currentSupplier?.id]
  );

  // Initialize form with existing task details and fetch buyer details when modal opens
  useEffect(() => {
    if (open && activity) {
      if (activity.taskDetails) {
        setTitle(activity.taskDetails.title || '');
        setPriority(activity.taskDetails.priority || 'MEDIUM');
        setStatus(activity.taskDetails.status || 'PENDING');
        setTaskType((activity.taskDetails as any)?.taskType || 'TO_DO');
        setAssignedTo(activity.taskDetails?.assignedTo || '');

        if (activity.taskDetails.dueDate) {
          setDueDate(new Date(activity.taskDetails.dueDate));
        } else {
          setDueDate(null);
        }
      }
      setReferences(activity.references || []);
      fetchBuyerDetails(activity.buyerId);
      // Fetch supplier users if not already loaded
      if (currentSupplier?.id && supplierUsers.length === 0) {
        dispatch(getSupplierSummary({ supplierId: currentSupplier.id }));
      }
    }
  }, [open, activity, fetchBuyerDetails, dispatch, currentSupplier?.id, supplierUsers.length]);

  const handleDescriptionChange = useCallback((content: string) => {
    setDescription(content || undefined);
  }, []);

  const handleSubmit = async () => {
    if (!title.trim()) {
      setTitleError('Please enter a task title');
      return;
    }

    if (!currentSupplier?.id || !activity) {
      return;
    }

    if (!user?.id || !activity) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if task is being marked as completed
      const wasCompleted = activity.taskDetails?.status === 'COMPLETED';
      const isNowCompleted = status === 'COMPLETED';

      const descContent = description || initialDescription;
      const updateData: any = {
        taskDetails: {
          title,
          description: descContent ? JSON.parse(descContent) : undefined,
          dueDate: dueDate || undefined,
          status,
          priority,
          taskType,
          assignedTo: assignedTo || null,
          completedAt: activity.taskDetails?.completedAt,
          completedBy: activity.taskDetails?.completedBy,
        },
        references,
      };

      // Set completion metadata if newly completed
      if (!wasCompleted && isNowCompleted) {
        updateData.taskDetails.completedAt = new Date();
        updateData.taskDetails.completedBy = user.id;
      }
      // Clear completion metadata if uncompleting
      else if (wasCompleted && !isNowCompleted) {
        updateData.taskDetails.completedAt = undefined;
        updateData.taskDetails.completedBy = undefined;
      }

      const result = await dispatch(
        updateCollectionActivity({
          supplierId: currentSupplier.id,
          activityId: activity.id,
          updateData,
        })
      );

      if (result.payload && (result.payload as any).success) {
        // Refresh the activities list
        dispatch(
          getBuyerCollectionActivities({
            supplierId: currentSupplier.id,
            buyerId: activity.buyerId,
          })
        );
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Failed to update task:', error);
      dispatch(
        openMessageModal({
          title: 'Error',
          subTitle: 'Failed to update task. Please try again.',
          buttonText: 'OK',
          modalType: 'ERROR',
        })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // Build assigned-to options from supplier users with unassign option
  const assignedToOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [{ value: '', label: 'Unassigned' }];

    // Add supplier users
    for (const u of supplierUsers) {
      options.push({
        value: u.id,
        label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      });
    }

    // If supplier users list is empty, fall back to assignedToUser from the activity
    if (supplierUsers.length === 0 && (activity as any)?.assignedToUser) {
      const assigned = (activity as any).assignedToUser;
      const label =
        `${assigned.firstName || ''} ${assigned.lastName || ''}`.trim() ||
        assigned.email ||
        assigned.id;
      if (!options.some((o) => o.value === assigned.id)) {
        options.push({ value: assigned.id, label });
      }
    }

    return options;
  }, [supplierUsers, activity]);

  // Format date for display
  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Edit Task"
      maxWidth="576px"
      footer={
        <>
          <DillButton
            onClick={handleClose}
            variant="secondary"
            isDisabled={isLoading}
            label="Cancel"
          />
          <DillButton
            onClick={handleSubmit}
            variant="primary"
            isLoading={isLoading}
            label="Save Changes"
          />
        </>
      }>
      <AppInputField
        label="Task Title"
        id="title"
        value={title}
        onTextChange={(val) => {
          setTitle(val);
          if (titleError) setTitleError('');
        }}
        placeholder="Enter task title..."
        isDisabled={isLoading}
        isRequired
        errorText={titleError}
      />

      <LexicalEditor
        label="Description (Optional)"
        initialContent={initialDescription}
        onChange={handleDescriptionChange}
        placeholder="Enter task description..."
        editable={!isLoading}
        minHeight="80px"
      />

      <div className="grid grid-cols-2 gap-4">
        <AppDatePicker
          label="Due Date (Optional)"
          selectedDate={dueDate}
          onDateSelected={(date) => setDueDate(date ?? null)}
          isDisabled={isLoading}
        />
        <AppDropDown
          label="Priority"
          items={priorityOptions}
          value={priority}
          onSelectChange={(selected) => setPriority(selected?.value as TaskPriority)}
          isDisabled={isLoading}
          placeholder="Select priority"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <AppDropDown
          label="Task Type"
          items={taskTypeOptions}
          value={taskType}
          onSelectChange={(selected) => setTaskType(selected?.value as TaskType)}
          isDisabled={isLoading}
          placeholder="Select task type"
        />
        <AppDropDown
          label="Status"
          items={statusOptions}
          value={status}
          onSelectChange={(selected) => setStatus(selected?.value as TaskStatus)}
          isDisabled={isLoading}
          placeholder="Select status"
        />
      </div>

      <AppDropDown
        label="Assigned To"
        items={assignedToOptions}
        value={assignedTo}
        onSelectChange={(selected) => setAssignedTo(selected?.value || '')}
        isDisabled={isLoading}
        placeholder="Select team member"
      />

      {activity?.taskDetails?.completedAt && (
        <div className="text-xs text-gray-500">
          Completed on {formatDate(activity.taskDetails.completedAt)}
        </div>
      )}

      <ActivityReferenceSelector
        label="References (Optional)"
        buyerId={activity?.buyerId || ''}
        value={references}
        onChange={setReferences}
        isDisabled={isLoading}
        contacts={buyerContacts}
      />
    </DillModal>
  );
}

export default EditTaskModal;
